import React, { useState } from "react";
import { get, isEmpty, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { dbDate, langDate } from "../../../../utils/properties";
import { Alert, Col, DatePicker, Input, InputNumber, Modal, Row, Select, Skeleton, Switch } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import CarFuelRefund from "../../../../services/api/CarFuelRefund";
import { showError } from "../../../../utils/utilities";
import CarUser from "../../../../services/api/CarUser";
import FuelCard from "../../../../services/api/FuelCard";
import dayjs from "dayjs";

const { Option } = Select;
const { confirm } = Modal;

function Km({ form, detailFolder, modalViewData, userInfo, canEdit }) {
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const [state, setState] = useState({
    carFuelRefundList: [],
    parkAuto: {},
    fuelCard: {},
    loadingData: false,
  });

  useEffect(() => {
    if ((form?.values?.privateCar || (modalViewData && modalViewData.privateCar)) && isEmpty(state.carFuelRefundList)) {
      getCarFuelRefundList();
    }
    if (modalViewData) {
      getCarUser(modalViewData.docDate);
    } else if (form?.values?.docDate) {
      getCarUser(dayjs(form?.values?.docDate, dateFormat).format(dbDate));
    }
  }, [form?.values?.privateCar, modalViewData]);

  const getCarFuelRefundList = async () => {
    const { data, error } = await CarFuelRefund.getCarFuelRefunds("allPages=true");
    if (!error && data) {
      setState((prevState) => ({ ...prevState, carFuelRefundList: data }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
  };

  const getCarUser = async (date) => {
    let parkAuto = {};
    let fuelCard = {};
    setState((prevState) => ({ ...prevState, loadingData: true }));
    const { data, error } = await CarUser.getCarUsers(`user.username=${userInfo.username}&date=${date}`);
    if (!error && data) {
      const { data: dataFuelCard, error: errorFuelCard } = await FuelCard.getFuelCards(
        `user.username=${userInfo.username}&date=${date}`
      );
      if (!isEmpty(data)) {
        parkAuto = data[0]?.parkAuto;
        form?.setFieldValue("parkAuto", data[0].parkAuto?.id);
      }
      if (!errorFuelCard) {
        if (!isEmpty(dataFuelCard)) {
          fuelCard = dataFuelCard[0];
          form?.setFieldValue("fuelCard", dataFuelCard[0]?.id);
        }
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, parkAuto, fuelCard, loadingData: false }));
  };

  const parkAutoRowContent = (form) => {
    if (!isEmpty(state.parkAuto)) {
      return (
        <Col>
          <p>
            {state.parkAuto.brand} {state.parkAuto.model} | {state.parkAuto.plate}
          </p>
        </Col>
      );
    } else {
      if (form?.values.docDate !== null) {
        if (state.loadingData) {
          return <Skeleton.Input size="small" />;
        } else {
          return t("expenseRefundFolder.kmInfoNoAuto");
        }
      } else {
        return t("expenseRefundFolder.kmInfoDateStart");
      }
    }
  };

  return (
    <>
      {
        <>
          {!modalViewData && (
            <Row>
              <Col span={24}>
                <Alert message={t("expenseRefundFolder.kmInfoDateBox")} type="info" />
              </Col>
            </Row>
          )}
          <Row gutter={[40, 24]} className="mb-10" justify="space-around">
            <Col lg={8} md={8} xs={24} sm={8}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="docDate">
                  {t("expenseRefundFolder.table.docDate")}*
                </label>
              </Row>
              <DatePicker
                disabled={modalViewData || !canEdit}
                className="w-100"
                allowClear={false}
                id="docDate"
                name="docDate"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  getCarUser(dayjs(date, dateFormat).format(dbDate));
                  form?.setFieldValue("docDate", date);
                }}
                onBlur={form?.handleBlur}
                value={modalViewData ? dayjs(modalViewData.docDate) : form?.values.docDate}
                disabledDate={(current) => {
                  // Set the minimum and maximum allowed dates
                  const minDate = dayjs(detailFolder?.periodStart);
                  const maxDate = dayjs(detailFolder?.periodEnd).add(1, "days");
                  // Disable all dates before minDate and after maxDate, except maxDate itself
                  return current && (current < minDate || current > maxDate);
                }}
              />
            </Col>
            <Col lg={10} md={10} xs={24} sm={10}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="parkAuto">
                  {t("expenseRefundFolder.table.parkAuto")}*
                </label>
              </Row>
              <Row>{parkAutoRowContent(form)}</Row>
            </Col>
            <Col lg={10} md={10} xs={24} sm={10}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="fuelCard">
                  {t("expenseRefundFolder.table.myFuelCard")}*
                </label>
              </Row>
              <Row>
                {!isEmpty(state.fuelCard) ? (
                  <Col>
                    <p>{state.fuelCard.cardNumber}</p>
                  </Col>
                ) : form?.values.docDate !== null && state.loadingData ? (
                  <Skeleton.Input size="small" />
                ) : form?.values.docDate !== null && !state.loadingData ? (
                  t("expenseRefundFolder.kmInfoNoFuelCard")
                ) : (
                  t("expenseRefundFolder.kmInfoDateStart")
                )}
              </Row>
            </Col>
            <Col lg={6} md={6} xs={24} sm={6}>
              <Row justify="center">
                <label className="fw-bold mt-10" htmlFor="privateCar">
                  {t("expenseRefundFolder.table.privateCar")}*
                </label>
              </Row>
              <Row justify="center">
                <Switch
                  id="privateCar"
                  name="privateCar"
                  disabled={modalViewData || !canEdit || form?.values.docDate === null}
                  onChange={(e) => {
                    form?.setFieldValue("arrival", null);
                    form?.setFieldValue("docNumber", null);
                    form?.setFieldValue("carPlate", null);
                    form?.setFieldValue("carFuelRefund", null);
                    if (e && !isEmpty(state.parkAuto)) {
                      confirm({
                        title: t("expenseRefundFolder.kmInfoBox"),
                        icon: <WarningOutlined />,
                        onOk: () => {
                          form?.setFieldValue("privateCar", e);
                          form?.setFieldValue("parkAuto", null);
                        },
                        onCancel() {},
                      });
                    } else {
                      form?.setFieldValue("privateCar", e);
                      if (e) {
                        form?.setFieldValue("parkAuto", null);
                      }
                    }
                  }}
                  onBlur={form?.handleBlur}
                  checked={modalViewData ? modalViewData.privateCar : form?.values.privateCar}
                  checkedChildren={t("general.yes")}
                  unCheckedChildren={t("general.no")}
                />
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 24]} className="mb-10" justify="space-around"></Row>
          <div
            style={{
              display: modalViewData
                ? modalViewData.privateCar
                  ? "block"
                  : "none"
                : form?.values.privateCar
                  ? "block"
                  : "none",
            }}
          >
            <Row gutter={[40, 24]} className="mb-10" justify="space-around">
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="departure">
                    {t("expenseRefundFolder.table.departure")}*
                  </label>
                </Row>
                <Row>
                  <Input
                    required
                    id="departure"
                    name="departure"
                    onChange={form?.handleChange}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.departure : form?.values.departure}
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                  />
                </Row>
              </Col>
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="arrival">
                    {t("expenseRefundFolder.table.arrival")}*
                  </label>
                </Row>
                <Row>
                  <Input
                    required
                    id="arrival"
                    name="arrival"
                    onChange={form?.handleChange}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.arrival : form?.values.arrival}
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                  />
                </Row>
              </Col>
            </Row>
            <Row gutter={[40, 24]} className="mb-10" justify="space-around">
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="docNumber">
                    {t("expenseRefundFolder.table.authNumber")}*
                  </label>
                </Row>
                <Row>
                  <Input
                    required
                    id="docNumber"
                    name="docNumber"
                    onChange={form?.handleChange}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.docNumber : form?.values.docNumber}
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                    maxLength={20}
                    showCount
                  />
                </Row>
              </Col>
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="carFuelRefund">
                    {t("expenseRefundFolder.table.carFuelRefund")}*
                  </label>
                </Row>
                <Row>
                  <Select
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                    className="w-100"
                    id="carFuelRefund"
                    name="carFuelRefund"
                    placeholder={t("expenseRefundFolder.table.carFuelRefund")}
                    onChange={(value) => {
                      form?.setFieldValue("carFuelRefund", value);
                    }}
                    onBlur={() => {
                      form?.handleBlur("carFuelRefund");
                    }}
                    value={modalViewData ? modalViewData.carFuelRefund?.id : form?.values.carFuelRefund}
                    showSearch
                    filterOption={(input, option) => option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  >
                    {state.carFuelRefundList.map((c, idx) => (
                      <Option
                        key={idx}
                        value={c.id}
                        name={`${c.ccFrom} - ${c.ccTo} | ${c.carEngineType.name}`}
                      >{`${c.ccFrom} - ${c.ccTo} | ${c.carEngineType.name}`}</Option>
                    ))}{" "}
                  </Select>
                </Row>
              </Col>
            </Row>
            <Row gutter={[40, 24]} className="mb-10" justify="space-around">
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="carPlate">
                    {t("expenseRefundFolder.table.carPlate")}*
                  </label>
                </Row>
                <Row>
                  <Input
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                    required
                    id="carPlate"
                    name="carPlate"
                    onChange={form?.handleChange}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.carPlate : form?.values.carPlate}
                  />
                </Row>
              </Col>
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="totalKm">
                    {t("expenseRefundFolder.table.totalKm")}*
                  </label>
                </Row>
                <InputNumber
                  disabled={modalViewData || !canEdit || form?.values.docDate === null}
                  className={modalViewData || form?.values.docDate === null ? "w-100" : "ant-input"}
                  controls={false}
                  required
                  id="totalKm"
                  name="totalKm"
                  onChange={(e) => form?.setFieldValue("totalKm", e)}
                  onBlur={form?.handleBlur}
                  value={modalViewData ? modalViewData.totalKm : form?.values.totalKm}
                  precision={0}
                />
              </Col>
            </Row>
            <Row gutter={[40, 24]} className="mb-10" justify="space-around">
              <Col lg={22} md={22} xs={24} sm={22}>
                <Row className="mb-10">
                  <label className="fw-bold mt-10" htmlFor="note">
                    {t("expenseRefundFolder.table.motivate")}*
                  </label>
                </Row>
                <Row>
                  <Input
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                    required
                    id="note"
                    name="note"
                    onChange={form?.handleChange}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.note : form?.values.note}
                    showCount
                    maxLength={20}
                  />
                </Row>
              </Col>
            </Row>
          </div>
          <div
            style={{
              display: modalViewData
                ? !modalViewData.privateCar
                  ? "block"
                  : "none"
                : !form?.values.privateCar
                  ? "block"
                  : "none",
            }}
          >
            <Row gutter={[40, 24]} className="mb-10" justify="space-around">
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="departure">
                    {t("expenseRefundFolder.table.departure")}*
                  </label>
                </Row>
                <Row>
                  <Input
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                    required
                    id="departure"
                    name="departure"
                    onChange={form?.handleChange}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.departure : form?.values.departure}
                  />
                </Row>
              </Col>
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="totalKm">
                    {t("expenseRefundFolder.table.totalKm")}*
                  </label>
                </Row>
                <InputNumber
                  disabled={modalViewData || !canEdit || form?.values.docDate === null}
                  className={modalViewData || form?.values.docDate === null ? "w-100" : "ant-input"}
                  controls={false}
                  required
                  id="totalKm"
                  name="totalKm"
                  onChange={(e) => form?.setFieldValue("totalKm", e)}
                  onBlur={form?.handleBlur}
                  value={modalViewData ? modalViewData.totalKm : form?.values.totalKm}
                  precision={0}
                />
              </Col>
            </Row>
            <Row gutter={[40, 24]} className="mb-10" justify="space-around">
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row>
                  <label className="fw-bold mt-10" htmlFor="amount">
                    {t("expenseRefundFolder.table.fuelEfficiency")}{" "}
                    {isEmpty(state.parkAuto)
                      ? ""
                      : ` (${state.parkAuto?.carEngineType?.fuel} | ${state.parkAuto?.carEngineType?.efficiencyDesc})`}
                    *
                  </label>
                </Row>
                <Row>
                  <InputNumber
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                    className={modalViewData || form?.values.docDate === null ? "w-100" : "ant-input"}
                    controls={false}
                    required
                    id="amount"
                    name="amount"
                    onChange={(e) => form?.setFieldValue("amount", e)}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.amount : form?.values.amount}
                    precision={2}
                    decimalSeparator=","
                  />
                </Row>
              </Col>
              <Col lg={10} md={10} xs={24} sm={10}>
                <Row className="mb-10">
                  <label className="fw-bold mt-10" htmlFor="note">
                    {t("expenseRefundFolder.table.motivate")}*
                  </label>
                </Row>
                <Row>
                  <Input
                    disabled={modalViewData || !canEdit || form?.values.docDate === null}
                    required
                    id="note"
                    name="note"
                    onChange={form?.handleChange}
                    onBlur={form?.handleBlur}
                    value={modalViewData ? modalViewData.note : form?.values.note}
                    showCount
                    maxLength={20}
                  />
                </Row>
              </Col>
            </Row>
          </div>
        </>
      }
    </>
  );
}
export default Km;
